import { enableProdMode } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { appConfig } from './app/app.config';
import { resolveBrand } from './app/core/config/brand.config';
import { environment } from './environments/environment';

if (environment.production) {
  enableProdMode()
}

const loadStylesheet = (href: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = () => resolve();
    link.onerror = () => reject(new Error(`Failed to load stylesheet: ${href}`));
    document.head.appendChild(link);
  });
};

const resolveHashedThemeFromNgsw = async (themeBundleName: string): Promise<string | null> => {
  try {
    const response = await fetch('/ngsw.json', { cache: 'no-store' });
    if (!response.ok) {
      return null;
    }

    const ngsw = await response.json() as { hashTable?: Record<string, string> };
    const files = Object.keys(ngsw.hashTable ?? {});
    const match = files.find((file) => file.startsWith(`/${themeBundleName}-`) && file.endsWith('.css'));
    return match ?? null;
  } catch {
    return null;
  }
};

const loadTheme = async (): Promise<void> => {
  const themeBundleName = 'app-theme';

  try {
    // Works for dev/local-debug where output hashing is disabled.
    await loadStylesheet(`/${themeBundleName}.css`);
    return;
  } catch {
    // Fall through to hashed-file resolution.
  }

  const hashedThemeFile = await resolveHashedThemeFromNgsw(themeBundleName);
  if (hashedThemeFile) {
    await loadStylesheet(hashedThemeFile);
    return;
  }

  throw new Error(`Could not resolve theme stylesheet for ${themeBundleName}`);
};

const bootstrap = async (): Promise<void> => {
  document.documentElement.setAttribute('data-brand', resolveBrand());
  await loadTheme();
  await bootstrapApplication(AppComponent, appConfig);
};

bootstrap().catch((err) => console.error(err));
